import { format } from 'date-fns';
import _ from 'lodash';

import { isCanadaLoc } from './locale';

import type { UserDispensariesAtom } from '../state/user-dispensaries';
import type { InventoryPackageAttributesFragment } from 'src/gql/graphql';

export function formatDate(value: any, dateTime = true, user: UserDispensariesAtom | false = false) {
  if (!value || value === '') {
    return '';
  }

  let val = value;
  if (typeof value === 'string') {
    val = new Date(value);
  }

  const locale = user && isCanadaLoc({ user }) ? 'en-CA' : 'en-US';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: dateTime ? 'numeric' : undefined,
    minute: dateTime ? 'numeric' : undefined,
    second: dateTime ? 'numeric' : undefined,
  }).format(val);
}

/**
 * Formats a date string as a readable format: "Tues, July 5th at 1:03pm PST"
 * @param dateString - ISO date string to format
 * @returns Formatted date string in readable format
 */
export function formatDateAsReadable(dateString: string): string {
  if (!dateString) {
    return '';
  }

  try {
    const date = new Date(dateString);

    // Check if the date is valid
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    // Format the main date part (day, month, date, time)
    const formattedDate = format(date, "eee, MMMM do 'at' h:mmaaa");

    // Get the abbreviated timezone using Intl.DateTimeFormat
    const timezone =
      new Intl.DateTimeFormat('en', {
        timeZoneName: 'short',
      })
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName')?.value || '';

    return `${formattedDate} ${timezone}`;
  } catch {
    return dateString;
  }
}

export function formatMoney(value: number | string, fallback?: number | string): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  const formattedVal = typeof value === 'string' ? value.replaceAll(',', '') : value;
  let workingVal = Number(formattedVal);
  if (Number.isNaN(workingVal)) {
    const fallBackNumber = Number(fallback);
    if (Number.isNaN(fallBackNumber)) {
      workingVal = 0;
    } else {
      workingVal = fallBackNumber;
    }
  }

  return formatter.format(workingVal);
}

export const formatNumberWithCommas = (number: number): string => {
  const num = Number(number);
  return num.toLocaleString('en');
};

export const formatNumberAsMoneyWithCommas = (number: number, maximumFractionDigits = 2): string => {
  const num = Number(number);
  return num.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits });
};

export const formatMgValue = (value: number | null | undefined): string => (value != null ? `${value}mg` : '--');

export const proudCamelCase = (str) =>
  str
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

export function normalizeYesNo(value) {
  if (value.toLowerCase() === 'yes') {
    return 'Yes';
  }

  if (value.toLowerCase() === 'no') {
    return 'No';
  }

  return value;
}

export const formatToMilitaryTime = (twelveHourFormatTimeString: string) => {
  const [time, modifier] = twelveHourFormatTimeString.split(' ');
  let hours = time.split(':')[0];
  const minutes = time.split(':')[1];
  if (hours === '12') {
    hours = '00';
  }
  if (modifier === 'PM') {
    hours = String(parseInt(hours, 10) + 12);
  }
  return `${hours}:${minutes}`;
};

export function toPascalCase(str: string) {
  return _.startCase(_.camelCase(str)).replace(/ /g, '');
}

export function updateKeysToPascalCase(obj) {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(updateKeysToPascalCase);
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      const pascalKey = toPascalCase(key);
      return [pascalKey, updateKeysToPascalCase(value)];
    })
  );
}

type FormatLabResultParams = {
  pkg: InventoryPackageAttributesFragment;
  unitKey: string;
  valueKey: string;
};

/**
 * @deprecated We should switch to using the new formatLabResults({ labResults, labKey }) helper
 * @see {@link src/app/utils/format-lab-results.ts} for the new implementation
 */
export function formatLabResult({ pkg, unitKey, valueKey }: FormatLabResultParams): string {
  const value = pkg.batch?.tblBatchThcs?.length ? pkg.batch?.tblBatchThcs[0]?.[valueKey] : null;
  const unitId = pkg.batch?.tblBatchThcs?.length ? pkg.batch?.tblBatchThcs[0]?.[unitKey] : null;

  if (value === null && unitId !== 4 && unitId !== 5) {
    return '';
  }

  switch (unitId) {
    case 5:
      return '<LOQ';
    case 4:
      return 'n.d.';
    case 3:
      return `${String(value?.toFixed(2))} mg/g`;
    case 2:
      return `${String(value?.toFixed(2))} %`;
    case 1:
      return `${String(value?.toFixed(2))} mg`;
    default:
      return '';
  }
}

export function formatOxfordCommaList(words: string[]): string {
  const nonEmptyWords = words.filter((word) => word);
  if (nonEmptyWords.length === 0) {
    return '';
  }
  if (nonEmptyWords.length === 1) {
    return words[0];
  }

  if (nonEmptyWords.length === 2) {
    return `${nonEmptyWords[0]} and ${nonEmptyWords[1]}`;
  }

  const lastWord = nonEmptyWords.pop() ?? '';
  return `${nonEmptyWords.join(', ')}, and ${lastWord}`;
}

export function capitalizeFirstLetter(str: string) {
  return str[0].toUpperCase() + str.slice(1);
}

export class Millisecond {
  static Millisecond = 1;
  static Second = 1_000;
  static Minute = Millisecond.Second * 60;
  static Hour = Millisecond.Minute * 60;
  static Day = Millisecond.Hour * 24;
  static Week = Millisecond.Day * 7;
  static Month = Millisecond.Day * 31;
  static Year = Millisecond.Day * 365;
}

export function formatMilliseconds(
  milliseconds: number,
  format?: (values: { hours: number; milliseconds: number; minutes: number; seconds: number }) => string
): string {
  format ??= ({ hours, minutes, seconds }) => `${hours}:${minutes}:${seconds}`;

  if (!milliseconds) {
    return format({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
  }

  const hours = Math.floor(milliseconds / Millisecond.Hour);
  milliseconds %= Millisecond.Hour;
  const minutes = Math.floor(milliseconds / Millisecond.Minute);
  milliseconds %= Millisecond.Minute;
  const seconds = Math.floor(milliseconds / Millisecond.Second);
  milliseconds %= Millisecond.Second;

  return format({ hours, minutes, seconds, milliseconds });
}
